import { etopoSoundingTool } from "../etopo-sounding/index.js";
import { crsPickerField, enhanceCrsPickers } from "../../core/ui/crs-picker.js";

// USACE eHydro: a catalog of condition-survey jobs, direct import of their data into HydroTools,
// and export of HydroTools soundings as an eHydro-named XYZ package. Research behind every URL,
// field name, and format claim below is in docs/research/ehydro-tool.md.
//
// The ArcGIS endpoint below has already moved once (the old spatial.usace.army.mil URL this file
// used to hit now 500s) and at least one other public project hit the same break -- if survey
// queries start failing, re-verify the base URL before assuming a code bug.
const SURVEY_FS = "https://services7.arcgis.com/n1YM8pTrFmm7L4hs/arcgis/rest/services/eHydro_Survey_Data/FeatureServer/0";
const CHANNEL_REACH_FS = "https://services7.arcgis.com/n1YM8pTrFmm7L4hs/arcgis/rest/services/National_Channel_Framework/FeatureServer/2";

const SURVEY_TYPE_LABELS = {
  AD: "After Dredge", BD: "Before Dredge", CS: "Condition Survey", PA: "Placement Area",
  OT: "Other", NS: "No Status", XA: "Combination A", XB: "Combination B", XC: "Combination C",
};
const CATALOG_PAGE_SIZE = 25;
// Confirmed survey deliverable extensions: ASCII XYZ text (.xyz / thinned .ppxyz / high-density
// .axyz / .xyzz) is the one format already readable by the Soundings importer unchanged.
const POINT_EXT_RE = /\.(xyz|ppxyz|axyz|xyzz|csv|txt|pts)$/i;
const SHP_COMPONENT_RE = /\.(shp|shx|dbf|prj|sbn|sbx|cpg|qix|fbn|fbx|ain|aih|ixs|mxs|atx)$/i;

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const LAYERS = [
  {
    id: "ehydro-survey-job",
    name: "USACE eHydro Survey Job",
    type: "esri-feature",
    url: SURVEY_FS,
    opacity: 0.9,
    options: { simplifyFactor: 0.35, precision: 5, interactive: true, outFields: ["*"], attribution: "USACE eHydro" },
  },
  {
    id: "ehydro-channel-reach",
    name: "USACE eHydro Channel Reach",
    type: "esri-feature",
    url: CHANNEL_REACH_FS,
    opacity: 0.9,
    options: { simplifyFactor: 0.35, precision: 5, interactive: true, outFields: ["*"], attribution: "USACE eHydro" },
  },
];

function findAttr(attrs, keys) {
  for (const key of keys) {
    if (attrs[key] != null && String(attrs[key]).trim() !== "") return attrs[key];
    const alt = Object.keys(attrs).find((k) => String(k).toLowerCase() === String(key).toLowerCase());
    if (alt && attrs[alt] != null && String(attrs[alt]).trim() !== "") return attrs[alt];
  }
  return "";
}

// Real field names confirmed against the live FeatureServer schema (docs/research/ehydro-tool.md
// §2) come first; the older generic aliases stay as a fallback for ChannelReach/other layers whose
// schema wasn't pulled.
const NAME_KEYS = ["SDSFEATURENAME", "SURVEYNAME", "SURVEY_NAME", "NAME", "JOB_NAME", "REACHNAME", "REACH_NAME"];
const PROJECT_KEYS = ["SDSFEATUREDESCRIPTION", "PROJECT", "PROJECT_NAME", "PROJ_NAME"];
const SURVEY_ID_KEYS = ["SURVEYJOBIDPK", "SDSID", "SURVEYID", "SURVEY_ID", "OBJECTID", "GLOBALID"];
const DISTRICT_KEYS = ["USACEDISTRICTCODE", "DISTRICT", "DIST_NAME", "DISTRICT_NAME"];
const SURVEY_TYPE_KEYS = ["SURVEYTYPE"];
const START_DATE_KEYS = ["SURVEYDATESTART", "SURVEY_DATE", "START_DATE", "DATE"];
const END_DATE_KEYS = ["SURVEYDATEEND", "END_DATE", "SURVEY_END", "ENDDATE"];
const SOURCE_DATA_KEYS = ["SOURCEDATALOCATION"];
const PLOT_SHEET_KEYS = ["PLOTSHEETLOCATION"];
const SOURCE_CONTENT_KEYS = ["SOURCEDATACONTENT"];
const SOURCE_PROJECTION_KEYS = ["SOURCEPROJECTION"];

function itemTitle(attrs) {
  return String(findAttr(attrs, NAME_KEYS) || findAttr(attrs, PROJECT_KEYS) || findAttr(attrs, SURVEY_ID_KEYS) || "eHydro Feature");
}

function parseDateMs(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!s) return null;
  const n = Number(s);
  if (Number.isFinite(n)) {
    if (n > 1e12) return n; // epoch ms (ArcGIS date fields come back this way over f=json)
    if (n > 1e9) return n * 1000;
    if (n >= 19000101 && n <= 21001231 && /^\d{8}$/.test(s)) {
      const y = Number(s.slice(0, 4)), m = Number(s.slice(4, 6)), d = Number(s.slice(6, 8));
      const ms = Date.UTC(y, Math.max(0, m - 1), d);
      return Number.isFinite(ms) ? ms : null;
    }
  }
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

function surveyDates(attrs) {
  const start = parseDateMs(findAttr(attrs, START_DATE_KEYS));
  const end = parseDateMs(findAttr(attrs, END_DATE_KEYS));
  return { start, end };
}

// Last resort for layers whose schema isn't the known SurveyJob one (ChannelReach etc.): scan for
// any date/survey-ish key, same heuristic the old version of this file used everywhere.
function fallbackDateMs(attrs) {
  for (const [k, v] of Object.entries(attrs || {})) {
    if (!/(date|time|survey)/i.test(String(k))) continue;
    if (/^(dateuploaded|datenotified|dateloadedenterprise)$/i.test(String(k))) continue; // upload bookkeeping, not survey date
    const ms = parseDateMs(v);
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

function fmtDate(ms) {
  if (!Number.isFinite(ms)) return "";
  try { return new Date(ms).toISOString().slice(0, 10); } catch { return ""; }
}

function fmtDateRange(attrs) {
  const { start, end } = surveyDates(attrs);
  if (start == null && end == null) {
    const ms = fallbackDateMs(attrs);
    return ms != null ? fmtDate(ms) : "Date unknown";
  }
  const a = fmtDate(start), b = fmtDate(end);
  if (a && b && a !== b) return `${a} to ${b}`;
  return a || b || "Date unknown";
}

function recencyMs(attrs) {
  const { end, start } = surveyDates(attrs);
  return end ?? start ?? fallbackDateMs(attrs);
}

function contentChips(attrs) {
  const raw = String(findAttr(attrs, SOURCE_CONTENT_KEYS) || "");
  if (!raw) return [];
  const exts = raw.split(",").map((s) => {
    const m = /\.([a-z0-9]+)\s*$/i.exec(s.trim());
    return (m ? m[1] : s.trim()).toLowerCase();
  }).filter(Boolean);
  return [...new Set(exts)].slice(0, 12);
}

function popupHtml(feature) {
  const attrs = feature?.properties || feature?.attributes || {};
  const name = itemTitle(attrs);
  const district = findAttr(attrs, DISTRICT_KEYS);
  const surveyType = String(findAttr(attrs, SURVEY_TYPE_KEYS) || "").toUpperCase();
  const surveyId = findAttr(attrs, SURVEY_ID_KEYS);
  const rows = [
    ["Name", name],
    ["Survey ID", surveyId],
    ["District", district],
    ["Survey type", surveyType ? `${surveyType} - ${SURVEY_TYPE_LABELS[surveyType] || "Unknown"}` : ""],
    ["Date", fmtDateRange(attrs)],
    ["Contains", contentChips(attrs).join(", ")],
  ].filter((r) => String(r[1] || "").trim());
  const extraKeys = Object.keys(attrs).slice(0, 20);
  const extras = extraKeys.map((k) => `<tr><td style="opacity:.75; padding:2px 6px 2px 0;">${esc(k)}</td><td style="padding:2px 0;">${esc(attrs[k])}</td></tr>`).join("");
  return `
    <div style="min-width:260px;">
      <div style="font-weight:700; margin-bottom:6px;">${esc(name)}</div>
      <table style="width:100%; border-collapse:collapse; font-size:12px;">
        ${rows.map((r) => `<tr><td style="opacity:.75; padding:2px 6px 2px 0; white-space:nowrap;">${esc(r[0])}</td><td style="padding:2px 0;">${esc(r[1])}</td></tr>`).join("")}
      </table>
      <details style="margin-top:6px;">
        <summary style="cursor:pointer;">Raw attributes</summary>
        <table style="width:100%; border-collapse:collapse; font-size:11px; margin-top:4px;">${extras}</table>
      </details>
    </div>
  `;
}

function rawAttrsPopup(title, attrs) {
  const rows = Object.entries(attrs || {}).slice(0, 30)
    .map(([k, v]) => `<tr><td style="opacity:.75; padding:2px 6px 2px 0; white-space:nowrap;">${esc(k)}</td><td style="padding:2px 0;">${esc(v)}</td></tr>`).join("");
  return `<div style="min-width:220px; max-width:360px;"><div style="font-weight:700; margin-bottom:6px;">${esc(title)}</div><table style="width:100%; border-collapse:collapse; font-size:11px;">${rows}</table></div>`;
}

function styleByRecency(feature) {
  const attrs = feature?.properties || feature?.attributes || {};
  const ms = recencyMs(attrs);
  const ageYears = Number.isFinite(ms) ? ((Date.now() - ms) / (1000 * 60 * 60 * 24 * 365.25)) : null;
  if (ageYears == null) return { color: "#94a3b8", fillColor: "#64748b", fillOpacity: 0.16, weight: 1.2 };
  if (ageYears <= 2) return { color: "#22c55e", fillColor: "#16a34a", fillOpacity: 0.22, weight: 1.4 };
  if (ageYears <= 5) return { color: "#eab308", fillColor: "#ca8a04", fillOpacity: 0.2, weight: 1.3 };
  if (ageYears <= 10) return { color: "#f97316", fillColor: "#ea580c", fillOpacity: 0.18, weight: 1.2 };
  return { color: "#ef4444", fillColor: "#b91c1c", fillOpacity: 0.14, weight: 1.1 };
}

function geojsonBoundsLatLng(geometry) {
  let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
  const visit = (coords, depth) => {
    if (depth === 0) {
      const [lon, lat] = coords;
      if (Number.isFinite(lon) && Number.isFinite(lat)) {
        if (lon < west) west = lon; if (lon > east) east = lon;
        if (lat < south) south = lat; if (lat > north) north = lat;
      }
      return;
    }
    for (const c of coords) visit(c, depth - 1);
  };
  const type = geometry?.type;
  const depthByType = { Point: 0, MultiPoint: 1, LineString: 1, MultiLineString: 2, Polygon: 2, MultiPolygon: 3 };
  if (type in depthByType) visit(geometry.coordinates, depthByType[type]);
  if (!Number.isFinite(west) || !Number.isFinite(south) || !Number.isFinite(east) || !Number.isFinite(north)) return null;
  return [[south, west], [north, east]];
}

// ── Catalog query against the live FeatureServer (CORS is open; confirmed in research) ──
function buildWhere(filters) {
  const clauses = [];
  const text = String(filters.text || "").trim();
  if (text) clauses.push(`UPPER(SDSFEATURENAME) LIKE UPPER('%${text.replace(/'/g, "''")}%')`);
  const district = String(filters.district || "").trim();
  if (district) clauses.push(`UPPER(USACEDISTRICTCODE) = UPPER('${district.replace(/'/g, "''")}')`);
  const surveyType = String(filters.surveyType || "").trim();
  if (surveyType) clauses.push(`UPPER(SURVEYTYPE) = UPPER('${surveyType.replace(/'/g, "''")}')`);
  const dateFrom = String(filters.dateFrom || "").trim();
  if (dateFrom) clauses.push(`SURVEYDATEEND >= timestamp '${dateFrom} 00:00:00'`);
  const dateTo = String(filters.dateTo || "").trim();
  if (dateTo) clauses.push(`SURVEYDATESTART <= timestamp '${dateTo} 23:59:59'`);
  return clauses.length ? clauses.join(" AND ") : "1=1";
}

async function queryCatalogPage(filters, offset, pageSize) {
  const params = {
    f: "geojson", where: buildWhere(filters), outFields: "*", outSR: "4326",
    resultRecordCount: String(pageSize), resultOffset: String(offset),
  };
  if (filters.withinView && filters.bounds) {
    Object.assign(params, {
      geometry: JSON.stringify(filters.bounds), geometryType: "esriGeometryEnvelope",
      inSR: "4326", spatialRel: "esriSpatialRelIntersects",
    });
  }
  const run = async (withOrder) => {
    const url = new URL(`${SURVEY_FS}/query`);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    if (withOrder) url.searchParams.set("orderByFields", "surveydateend DESC");
    const res = await fetch(url.toString());
    return res.json();
  };
  let json = await run(true);
  if (json?.error || !Array.isArray(json?.features)) json = await run(false);
  if (json?.error) throw new Error(json.error.message || "eHydro query failed.");
  return Array.isArray(json?.features) ? json.features : [];
}

async function queryCatalogCount(filters) {
  const url = new URL(`${SURVEY_FS}/query`);
  url.searchParams.set("f", "json");
  url.searchParams.set("where", buildWhere(filters));
  url.searchParams.set("returnCountOnly", "true");
  if (filters.withinView && filters.bounds) {
    url.searchParams.set("geometry", JSON.stringify(filters.bounds));
    url.searchParams.set("geometryType", "esriGeometryEnvelope");
    url.searchParams.set("inSR", "4326");
    url.searchParams.set("spatialRel", "esriSpatialRelIntersects");
  }
  const res = await fetch(url.toString());
  const json = await res.json();
  return Number.isFinite(Number(json?.count)) ? Number(json.count) : null;
}

async function loadDistrictOptions() {
  try {
    const url = new URL(`${SURVEY_FS}/query`);
    url.searchParams.set("f", "json");
    url.searchParams.set("where", "1=1");
    url.searchParams.set("outFields", "usacedistrictcode");
    url.searchParams.set("returnGeometry", "false");
    url.searchParams.set("returnDistinctValues", "true");
    url.searchParams.set("orderByFields", "usacedistrictcode");
    url.searchParams.set("resultRecordCount", "100");
    const res = await fetch(url.toString());
    const json = await res.json();
    const vals = (json?.features || []).map((f) => f?.attributes?.usacedistrictcode).filter(Boolean);
    return [...new Set(vals)].sort();
  } catch {
    return [];
  }
}

// USACE's SOURCEPROJECTION is a prose name ("NAD_1983_StatePlane_Kentucky_North_FIPS_1601_Feet"),
// not an EPSG code -- guess against the app's own verified EPSG catalogue rather than hand-coding a
// state-plane-name -> EPSG table (wrong entries there would silently misplace survey data).
function guessCrsFromSourceProjection(app, text) {
  const svc = app?.services?.projections;
  const raw = String(text || "").trim();
  if (!raw || typeof svc?.searchCRS !== "function") return null;
  try {
    const results = svc.searchCRS(raw.replace(/_/g, " "), { limit: 5 }) || [];
    return results[0]?.code || null;
  } catch {
    return null;
  }
}

function promptImportCrs(app, { guessedCode, hintText } = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed; inset:0; z-index:5000; background:rgba(2,6,23,.65); display:grid; place-items:center;";
    const card = document.createElement("div");
    card.className = "card";
    card.style.cssText = "width:min(520px, calc(100vw - 32px)); padding:12px;";
    card.innerHTML = `
      <div style="font-weight:700; margin-bottom:8px;">Confirm the survey's coordinate system</div>
      <div class="small" style="margin-bottom:8px; color:var(--muted);">
        USACE states this survey's projection as free text, not an EPSG code${hintText ? `: <b>${esc(hintText)}</b>` : "."}
        ${guessedCode ? " A best match is pre-filled below -- check it before importing." : " No automatic match was found -- pick the correct CRS."}
      </div>
      <div style="margin-bottom:10px;">${crsPickerField("ehy-import-crs-pick", String(guessedCode || "EPSG:4326").toUpperCase(), { style: "width:100%" })}</div>
      <div class="row" style="justify-content:flex-end; gap:6px;">
        <button id="ehy-crs-cancel" class="btn">Cancel</button>
        <button id="ehy-crs-ok" class="btn primary">Import</button>
      </div>
    `;
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    enhanceCrsPickers(card, app);
    const close = (value) => { try { overlay.remove(); } catch {} resolve(value); };
    card.querySelector("#ehy-crs-cancel")?.addEventListener("click", () => close(null));
    card.querySelector("#ehy-crs-ok")?.addEventListener("click", () => {
      const picked = card.querySelector("#ehy-import-crs-pick")?.value;
      close(String(picked || "").trim().toUpperCase() || null);
    });
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(null); });
  });
}

// ── ZIP classification (confirmed real contents: docs/research/ehydro-tool.md §3). Some
// districts bundle their shapefile as its own nested "<name>_SHP.zip" rather than loose
// components (confirmed live, CEMVN) -- recurse into any .zip entry one level deep.
async function classifyZipEntries(zip, depth = 0) {
  const all = Object.values(zip.files || {}).filter((f) => !f.dir);
  const points = [];
  const gdbEntries = [];
  const shpGroups = new Map();
  const kmlEntries = [];
  const xmlEntries = [];
  const otherEntries = [];
  for (const entry of all) {
    const name = entry.name;
    if (/\.gdb\//i.test(name) || /\.gdb$/i.test(name)) { gdbEntries.push(entry); continue; }
    if (POINT_EXT_RE.test(name)) { points.push(entry); continue; }
    if (/\.xml$/i.test(name)) { xmlEntries.push(entry); continue; }
    if (/\.(kml|kmz)$/i.test(name)) { kmlEntries.push(entry); continue; }
    if (SHP_COMPONENT_RE.test(name)) {
      const base = `${name.replace(/\.[^./]+$/, "")}`;
      if (!shpGroups.has(base)) shpGroups.set(base, []);
      shpGroups.get(base).push(entry);
      continue;
    }
    if (/\.pdf$/i.test(name)) continue; // the plot sheet is already linked directly from PLOTSHEETLOCATION
    if (/\.zip$/i.test(name) && depth < 2) {
      try {
        const { default: JSZip } = await import("jszip");
        const nestedZip = await JSZip.loadAsync(await entry.async("uint8array"));
        const nested = await classifyZipEntries(nestedZip, depth + 1);
        points.push(...nested.points);
        gdbEntries.push(...nested.gdbEntries);
        kmlEntries.push(...nested.kmlEntries);
        xmlEntries.push(...nested.xmlEntries);
        for (const [base, entries] of nested.shpGroups) shpGroups.set(`${name}::${base}`, entries);
        otherEntries.push(...nested.otherEntries);
      } catch {
        otherEntries.push(entry);
      }
      continue;
    }
    otherEntries.push(entry);
  }
  return { points, gdbEntries, shpGroups, kmlEntries, xmlEntries, otherEntries };
}

function pickPrimaryPointEntry(points) {
  const plain = points.find((e) => /\.xyz$/i.test(e.name) && !/ppxyz$/i.test(e.name));
  return plain || points[0] || null;
}

async function convertVectorBundleOnServer(entries) {
  const { default: JSZip } = await import("jszip");
  const bundle = new JSZip();
  for (const entry of entries) {
    bundle.file(entry.name, await entry.async("uint8array"));
  }
  const bytes = await bundle.generateAsync({ type: "uint8array" });
  const res = await fetch("/api/ehydro/convert-vector", { method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: bytes });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || `Conversion failed (HTTP ${res.status}).`);
  return json;
}

function extractXmlMetadataSnippet(xmlText) {
  const text = String(xmlText || "");
  const tag = (name) => { const m = new RegExp(`<${name}>([\\s\\S]*?)<\\/${name}>`, "i").exec(text); return m ? m[1].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : ""; };
  const standard = tag("metstdn") || (/<gmd:MD_Metadata/i.test(text) ? "ISO 19115" : "");
  const vertdef = tag("vertdef");
  const abstract = tag("abstract").slice(0, 500);
  return { standard, vertdef: vertdef.slice(0, 400), abstract };
}

export const ehydroTool = {
  id: "ehydro",
  name: "USACE eHydro",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let status = "Ready.";
  let stackPickEnabled = true;
  let mapClickBound = false;
  let mapDomClickBound = false;
  let mapDomHost = null;
  let docClickBound = false;
  let importBusy = false;
  let importProgress = { active: false, ratio: 0, message: "" };
  let importLog = []; // most recent first: { title, ok, message, at }

  let districtOptions = [];
  let catalogFilters = { text: "", district: "", surveyType: "", dateFrom: "", dateTo: "", withinView: false };
  let catalogResults = [];
  let catalogOffset = 0;
  let catalogLoading = false;
  let catalogError = "";
  let catalogHasMore = true;
  let catalogTotalCount = null;
  let searchDebounce = null;

  let soundingsLayers = [];
  let exportForm = {
    layerId: "", projectCode: "", uniqueId: "", subproject: "", surveyType: "CS", description: "",
    crs: "EPSG:4326", depthUnit: "ft", precision: 2, thinned: false, thinFactor: 10, verticalDatum: "",
  };
  let exportBusy = false;
  let exportStatus = "";

  function setImportProgress(active, ratio = 0, message = "") {
    importProgress = { active: !!active, ratio: Math.max(0, Math.min(1, Number(ratio) || 0)), message: String(message || "") };
    render();
  }

  function pushLog(title, ok, message) {
    importLog = [{ title, ok, message, at: new Date().toISOString() }, ...importLog].slice(0, 8);
  }

  const layerService = () => app?.services?.layers || null;
  const existingByMetaId = (metaId) => (layerService()?.listLayers?.() || []).find((layer) => String(layer?.meta?.ehydroId || "") === String(metaId));

  function addLayer(def) {
    const svc = layerService();
    if (!svc) { status = "Layer service unavailable."; render(); return; }
    const existing = existingByMetaId(def.id);
    if (existing?.id) { status = `${def.name} already loaded.`; render(); return; }
    svc.addLayer("ehydro", {
      id: def.id, kind: "overlay", type: def.type, name: def.name, url: def.url, visible: true, opacity: def.opacity,
      options: { ...(def.options || {}), popupBuilder: popupHtml, styleBuilder: styleByRecency },
      meta: { sourceType: "ehydro", ehydroId: def.id },
    });
    status = `Loaded ${def.name}.`;
    render();
  }

  function removeLayer(def) {
    const svc = layerService();
    const existing = existingByMetaId(def.id);
    if (!svc || !existing?.id) { status = `${def.name} not loaded.`; render(); return; }
    svc.removeLayer(existing.id);
    status = `Removed ${def.name}.`;
    render();
  }

  const isLoaded = (def) => !!existingByMetaId(def.id);
  const loadedDefs = () => LAYERS.filter((def) => isLoaded(def));

  let importedLayerSeq = 0;
  function addGeojsonResultLayer(name, geojson, sourceLabel) {
    const svc = layerService();
    if (!svc || !geojson?.features?.length) return;
    const id = `ehydro-import-${Date.now().toString(36)}-${importedLayerSeq++}`;
    svc.addLayer("ehydro", {
      id, kind: "overlay", type: "geojson-vector", name, data: geojson, visible: true, opacity: 0.9,
      options: { popupBuilder: (feature) => rawAttrsPopup(name, feature?.properties || {}) },
      meta: { sourceType: "ehydro-import", ehydroSource: sourceLabel },
    });
  }

  // ── stacked click-picker on the map (unchanged in spirit from the beta version) ──
  async function queryAtLatLng(def, lat, lon) {
    const q = new URL(`${def.url.replace(/\/+$/, "")}/query`);
    q.searchParams.set("f", "json");
    q.searchParams.set("where", "1=1");
    q.searchParams.set("geometryType", "esriGeometryPoint");
    q.searchParams.set("spatialRel", "esriSpatialRelIntersects");
    q.searchParams.set("inSR", "4326");
    q.searchParams.set("outSR", "4326");
    q.searchParams.set("returnGeometry", "false");
    q.searchParams.set("outFields", "*");
    q.searchParams.set("geometry", JSON.stringify({ x: lon, y: lat, spatialReference: { wkid: 4326 } }));
    const res = await fetch(q.toString());
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return Array.isArray(json?.features) ? json.features : [];
  }

  async function onMapClick(e) {
    if (!stackPickEnabled) return;
    const defs = loadedDefs();
    if (!defs.length) return;
    const lat = Number(e?.latlng?.lat);
    const lon = Number(e?.latlng?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    try {
      const results = await Promise.all(defs.map(async (def) => {
        try {
          const features = await queryAtLatLng(def, lat, lon);
          return features.map((f) => ({ def, attrs: f?.attributes || f?.properties || {} }));
        } catch { return []; }
      }));
      const hits = results.flat();
      if (!hits.length) return;
      const rows = hits
        .map((hit) => ({ ...hit, timeMs: recencyMs(hit.attrs || {}) }))
        .sort((a, b) => (Number(b.timeMs || -Infinity) - Number(a.timeMs || -Infinity)))
        .slice(0, 80)
        .map((hit) => {
          const attrs = hit.attrs || {};
          const title = itemTitle(attrs);
          const surveyId = String(findAttr(attrs, SURVEY_ID_KEYS) || title).trim();
          const dataUrl = String(findAttr(attrs, SOURCE_DATA_KEYS) || "").trim();
          const plotUrl = String(findAttr(attrs, PLOT_SHEET_KEYS) || "").trim();
          const sourceProjection = String(findAttr(attrs, SOURCE_PROJECTION_KEYS) || "").trim();
          return `
            <details style="margin-top:4px;">
              <summary style="cursor:pointer;"><span style="font-weight:700;">${esc(fmtDateRange(attrs))}</span> - ${esc(surveyId)}</summary>
              <div class="small" style="margin-top:4px;">
                ${dataUrl ? `<div><button class="btn small" data-ehydro-import-url="${esc(dataUrl)}" data-ehydro-import-name="${esc(surveyId)}" data-ehydro-import-proj="${esc(sourceProjection)}" data-ehydro-import-lat="${esc(lat)}" data-ehydro-import-lon="${esc(lon)}">Import survey data</button></div>` : "<div>No data link on this record.</div>"}
                ${plotUrl ? `<div><a href="${esc(plotUrl)}" target="_blank" rel="noopener noreferrer">Plot sheet (PDF)</a></div>` : ""}
              </div>
            </details>
          `;
        }).join("");
      const html = `
        <div style="min-width:320px; max-height:320px; overflow:auto;">
          <div style="font-weight:700; margin-bottom:4px;">eHydro surveys at click: ${hits.length} (newest first)</div>
          ${rows}
        </div>
      `;
      const map = app?.map?.instance;
      if (map && window.L?.popup) window.L.popup({ maxWidth: 420 }).setLatLng(e.latlng).setContent(html).openOn(map);
    } catch {}
  }

  async function onMapDomClick(ev) {
    if (!stackPickEnabled) return;
    const defs = loadedDefs();
    if (!defs.length) return;
    const target = ev?.target;
    if (target?.closest?.(".leaflet-popup")) return;
    if (target?.closest?.(".leaflet-control")) return;
    if (!target?.closest?.(".leaflet-interactive")) return;
    const map = app?.map?.instance;
    if (!map || typeof map.mouseEventToLatLng !== "function") return;
    const latlng = map.mouseEventToLatLng(ev);
    if (!latlng) return;
    ev.preventDefault?.();
    ev.stopPropagation?.();
    try { if (window.L?.DomEvent?.stop) window.L.DomEvent.stop(ev); } catch {}
    await onMapClick({ latlng });
  }

  async function onDocumentClick(ev) {
    const btn = ev?.target?.closest?.("[data-ehydro-import-url]");
    if (!btn) return;
    ev.preventDefault?.();
    ev.stopPropagation?.();
    const href = btn.getAttribute("data-ehydro-import-url") || "";
    const name = btn.getAttribute("data-ehydro-import-name") || "eHydro Survey";
    const sourceProjection = btn.getAttribute("data-ehydro-import-proj") || "";
    const lat = Number(btn.getAttribute("data-ehydro-import-lat"));
    const lon = Number(btn.getAttribute("data-ehydro-import-lon"));
    await runImport({ sourceUrl: href, name, sourceProjection, lat, lon });
  }

  function ensureMapClickBinding() {
    const map = app?.map?.instance;
    if (!map || mapClickBound) return;
    map.on("click", onMapClick);
    mapClickBound = true;
    const host = map.getContainer?.();
    if (host && !mapDomClickBound) { host.addEventListener("click", onMapDomClick, true); mapDomHost = host; mapDomClickBound = true; }
    if (!docClickBound) { document.addEventListener("click", onDocumentClick, true); docClickBound = true; }
  }

  function removeMapClickBinding() {
    const map = app?.map?.instance;
    if (!map || !mapClickBound) return;
    map.off("click", onMapClick);
    mapClickBound = false;
    if (mapDomHost && mapDomClickBound) mapDomHost.removeEventListener("click", onMapDomClick, true);
    if (docClickBound) document.removeEventListener("click", onDocumentClick, true);
    mapDomHost = null; mapDomClickBound = false; docClickBound = false;
  }

  // ── unified import pipeline: one survey's data -> soundings + converted vector layers ──
  async function runImport({ sourceUrl, name, sourceProjection, lat, lon }) {
    if (importBusy) return;
    importBusy = true;
    const label = String(name || "eHydro Survey").trim() || "eHydro Survey";
    try {
      setImportProgress(true, 0.02, "Downloading survey ZIP...");
      const res = await fetch(sourceUrl);
      if (!res.ok) throw new Error(`Download failed (HTTP ${res.status}).`);
      const buf = await res.arrayBuffer();
      setImportProgress(true, 0.3, "Reading ZIP contents...");
      const { default: JSZip } = await import("jszip");
      const zip = await JSZip.loadAsync(buf);
      const { points, gdbEntries, shpGroups, kmlEntries, xmlEntries, otherEntries } = await classifyZipEntries(zip);
      if (!points.length && !gdbEntries.length && !shpGroups.size && !kmlEntries.length) {
        throw new Error("No recognized survey data (XYZ, geodatabase, shapefile, or KML/KMZ) found in this ZIP.");
      }

      let metaNote = "";
      if (xmlEntries.length) {
        try {
          const xmlText = await xmlEntries[0].async("text");
          const snip = extractXmlMetadataSnippet(xmlText);
          if (snip.vertdef) metaNote = `Vertical datum (from bundled metadata XML): ${snip.vertdef}`;
          else if (snip.standard) metaNote = `Bundled metadata standard: ${snip.standard}`;
        } catch {}
      }

      const parts = [];

      if (points.length) {
        const primary = pickPrimaryPointEntry(points);
        setImportProgress(true, 0.35, "Confirming coordinate system...");
        const guessed = guessCrsFromSourceProjection(app, sourceProjection);
        const crs = await promptImportCrs(app, { guessedCode: guessed, hintText: sourceProjection });
        if (!crs) {
          pushLog(label, false, "Import cancelled at CRS confirmation.");
        } else {
          setImportProgress(true, 0.45, `Importing ${primary.name}...`);
          const text = await primary.async("text");
          try { etopoSoundingTool?.init?.(app); } catch {}
          const out = await new Promise((resolve, reject) => {
            let settled = false;
            const watchdog = setTimeout(() => { if (!settled) { settled = true; reject(new Error("Soundings importer did not respond. Open the Soundings tool once and retry.")); } }, 8000);
            app.events.emit("soundings:import:text", {
              text, sourceUrl, sourceCRS: crs, fileName: primary.name, layerName: label, zoom: true,
              hintLat: Number(lat), hintLon: Number(lon),
              progress: (p = {}) => setImportProgress(true, 0.45 + (Math.max(0, Math.min(1, Number(p?.ratio) || 0)) * 0.35), String(p?.message || "Parsing...")),
              resolve: (v) => { if (!settled) { settled = true; clearTimeout(watchdog); resolve(v); } },
              reject: (e) => { if (!settled) { settled = true; clearTimeout(watchdog); reject(e); } },
            });
          });
          const skippedOthers = points.length - 1;
          parts.push(`${Number(out?.pointCount || 0).toLocaleString()} soundings from ${primary.name}${skippedOthers ? ` (${skippedOthers} other point file(s) in the ZIP not imported)` : ""}`);
        }
      }

      if (gdbEntries.length) {
        setImportProgress(true, 0.85, "Converting geodatabase (server-side GDAL)...");
        try {
          const { layers } = await convertVectorBundleOnServer(gdbEntries);
          let added = 0;
          for (const layer of layers) {
            if (layer.geojson) { addGeojsonResultLayer(`${label} - GDB: ${layer.name}`, layer.geojson, "gdb"); added += 1; }
          }
          parts.push(added ? `${added} geodatabase layer(s) added to the map` : "geodatabase had no importable layers");
        } catch (err) {
          parts.push(`geodatabase conversion failed: ${err?.message || err}`);
        }
      }

      if (shpGroups.size) {
        setImportProgress(true, 0.92, "Converting shapefile(s)...");
        for (const [base, entries] of shpGroups) {
          if (!entries.some((e) => /\.shp$/i.test(e.name))) continue; // need the .shp itself, not just .dbf/.prj orphans
          try {
            const { layers } = await convertVectorBundleOnServer(entries);
            for (const layer of layers) {
              if (layer.geojson) addGeojsonResultLayer(`${label} - ${base.split("/").pop()}`, layer.geojson, "shp");
            }
          } catch (err) {
            parts.push(`${base.split("/").pop()}.shp conversion failed: ${err?.message || err}`);
          }
        }
      }

      if (kmlEntries.length) {
        setImportProgress(true, 0.96, "Converting KML/KMZ...");
        for (const entry of kmlEntries) {
          const base = entry.name.split("/").pop().replace(/\.[^./]+$/, "");
          try {
            const { layers } = await convertVectorBundleOnServer([entry]);
            let added = 0;
            for (const layer of layers) {
              if (layer.geojson) { addGeojsonResultLayer(`${label} - ${base}`, layer.geojson, "kml"); added += 1; }
            }
            if (added) parts.push(`${base} (${added} layer(s))`);
          } catch (err) {
            parts.push(`${base} conversion failed: ${err?.message || err}`);
          }
        }
      }

      if (otherEntries.length) {
        parts.push(`${otherEntries.length} other file(s) in the ZIP were not ingested (${[...new Set(otherEntries.map((e) => (e.name.match(/\.[^.]+$/)?.[0] || "?")))].join(", ")}) -- use the raw ZIP link for those`);
      }

      const message = [metaNote, parts.join("; ")].filter(Boolean).join(". ");
      pushLog(label, true, message || "Nothing imported.");
      status = message || "Nothing imported.";
    } catch (err) {
      pushLog(label, false, err?.message || String(err));
      status = `Import failed: ${err?.message || String(err)}`;
    } finally {
      importBusy = false;
      setImportProgress(false, 0, "");
      render();
    }
  }

  // ── catalog ──
  async function runCatalogSearch({ reset }) {
    if (catalogLoading) return;
    catalogLoading = true;
    catalogError = "";
    render();
    try {
      if (reset) {
        catalogOffset = 0;
        catalogResults = [];
        catalogHasMore = true;
        if (catalogFilters.withinView) catalogFilters.bounds = boundsEnvelope();
        catalogTotalCount = await queryCatalogCount(catalogFilters).catch(() => null);
      }
      const features = await queryCatalogPage(catalogFilters, catalogOffset, CATALOG_PAGE_SIZE);
      catalogResults = reset ? features : [...catalogResults, ...features];
      catalogOffset += features.length;
      catalogHasMore = features.length === CATALOG_PAGE_SIZE;
    } catch (err) {
      catalogError = err?.message || String(err);
    } finally {
      catalogLoading = false;
      render();
    }
  }

  function boundsEnvelope() {
    const b = app?.map?.instance?.getBounds?.();
    if (!b) return null;
    return { xmin: b.getWest(), ymin: b.getSouth(), xmax: b.getEast(), ymax: b.getNorth(), spatialReference: { wkid: 4326 } };
  }

  function zoomToFeature(feature) {
    const bounds = geojsonBoundsLatLng(feature?.geometry);
    const map = app?.map?.instance;
    if (bounds && map && window.L) map.fitBounds(window.L.latLngBounds(bounds).pad(0.15));
  }

  function addFeatureOutline(feature) {
    const attrs = feature?.properties || {};
    addGeojsonResultLayer(itemTitle(attrs), { type: "FeatureCollection", features: [{ ...feature, properties: attrs }] }, "catalog-outline");
  }

  // ── export ──
  function refreshSoundingsLayers() {
    app.events.emit("soundings:catalog:request");
  }

  function unitsFactor(unit) {
    if (unit === "ft") return 3937 / 1200; // US survey foot
    if (unit === "ftIntl") return 3.280839895;
    return 1;
  }

  function sanitizeToken(s) {
    return String(s || "").trim().replace(/[^A-Za-z0-9-]+/g, "_").replace(/^_+|_+$/g, "");
  }

  function fmtYYYYMMDD(d) {
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  }

  function ehydroBaseName(form) {
    const parts = [sanitizeToken(form.projectCode), sanitizeToken(form.uniqueId), sanitizeToken(form.subproject), fmtYYYYMMDD(new Date()), sanitizeToken(form.surveyType)];
    if (form.description) parts.push(sanitizeToken(form.description));
    return parts.filter(Boolean).join("_");
  }

  async function runExport() {
    if (exportBusy) return;
    const layer = soundingsLayers.find((l) => l.layerId === exportForm.layerId);
    if (!layer) { exportStatus = "Choose a Soundings layer to export."; render(); return; }
    if (!exportForm.projectCode || !exportForm.uniqueId || !exportForm.subproject) {
      exportStatus = "Project code, unique ID, and subproject are required by eHydro's naming convention.";
      render();
      return;
    }
    exportBusy = true;
    exportStatus = "Exporting...";
    render();
    try {
      const svc = app.services.projections;
      const crs = String(exportForm.crs || "EPSG:4326").toUpperCase();
      if (crs !== "EPSG:4326") await svc.ensureCRS?.(crs).catch(() => {});
      const depthFactor = unitsFactor(exportForm.depthUnit);
      const prec = Math.max(0, Math.min(6, Number(exportForm.precision) || 2));
      const rows = [];
      let skipped = 0;
      for (const r of (layer.records || [])) {
        let e = r.lon, n = r.lat;
        if (crs !== "EPSG:4326") {
          try {
            const out = svc.transformCoord([r.lon, r.lat], "EPSG:4326", crs);
            e = Number(out?.[0]); n = Number(out?.[1]);
          } catch { skipped += 1; continue; }
        }
        if (!Number.isFinite(e) || !Number.isFinite(n) || !Number.isFinite(Number(r.z))) { skipped += 1; continue; }
        rows.push(`${e.toFixed(prec)} ${n.toFixed(prec)} ${(Number(r.z) * depthFactor).toFixed(prec)}`);
      }
      if (!rows.length) throw new Error("No valid points to export.");
      const baseName = ehydroBaseName(exportForm);
      const mainText = rows.join("\n");
      const crsInfo = crs === "EPSG:4326" ? null : svc.getCRS?.(crs);
      const metaText = [
        "HydroTools eHydro-style XYZ export",
        `Generated: ${new Date().toISOString()}`,
        `Source layer (HydroTools Soundings): ${layer.name}`,
        `Point count exported: ${rows.length}${skipped ? ` (skipped ${skipped} point(s) that failed reprojection)` : ""}`,
        `Horizontal CRS: ${crs}${crsInfo?.name ? ` - ${crsInfo.name}` : ""}`,
        `Depth units: ${exportForm.depthUnit === "ft" ? "US survey feet" : exportForm.depthUnit === "ftIntl" ? "international feet" : "meters"} (positive down)`,
        `Vertical datum (as entered, not independently verified): ${exportForm.verticalDatum || "not specified"}`,
        `Survey type code: ${exportForm.surveyType} (${SURVEY_TYPE_LABELS[exportForm.surveyType] || "unknown"})`,
        "",
        "This package follows USACE eHydro's XYZ filename convention and basic Easting/Northing/Depth",
        "point format (see USACE's \"eHydro XYZ Filenames\" guide and Hypack's \"eHydro Naming\" XYZ",
        "export mode). It does NOT include the ISO 19115/FGDC metadata XML or Esri geodatabase your",
        "district's eHydro submission may also require -- confirm current requirements with your",
        "USACE district before submitting.",
      ].join("\n");
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      zip.file(`${baseName}.XYZ`, mainText);
      if (exportForm.thinned) {
        const factor = Math.max(2, Math.round(Number(exportForm.thinFactor) || 10));
        zip.file(`${baseName}_P.XYZ`, rows.filter((_, i) => i % factor === 0).join("\n"));
      }
      zip.file(`${baseName}_metadata.txt`, metaText);
      const blob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      const u = URL.createObjectURL(blob);
      a.href = u; a.download = `${baseName}.zip`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
      exportStatus = `Exported ${rows.length.toLocaleString()} points as ${baseName}.zip${skipped ? ` (${skipped} skipped)` : ""}.`;
    } catch (err) {
      exportStatus = `Export failed: ${err?.message || err}`;
    } finally {
      exportBusy = false;
      render();
    }
  }

  // ── render ──
  function catalogRowHtml(feature) {
    const attrs = feature?.properties || {};
    const name = itemTitle(attrs);
    const district = findAttr(attrs, DISTRICT_KEYS);
    const surveyType = String(findAttr(attrs, SURVEY_TYPE_KEYS) || "").toUpperCase();
    const dataUrl = String(findAttr(attrs, SOURCE_DATA_KEYS) || "").trim();
    const plotUrl = String(findAttr(attrs, PLOT_SHEET_KEYS) || "").trim();
    const sourceProjection = String(findAttr(attrs, SOURCE_PROJECTION_KEYS) || "").trim();
    const chips = contentChips(attrs);
    return `
      <div class="card" style="padding:8px; margin-bottom:6px;">
        <div class="row" style="justify-content:space-between; align-items:flex-start; gap:8px;">
          <div style="min-width:0;">
            <div style="font-weight:600;">${esc(name)}</div>
            <div class="small" style="opacity:.85;">
              ${district ? `${esc(district)} · ` : ""}${surveyType ? `${esc(surveyType)} (${esc(SURVEY_TYPE_LABELS[surveyType] || "?")}) · ` : ""}${esc(fmtDateRange(attrs))}
            </div>
            ${chips.length ? `<div class="small" style="margin-top:3px;">${chips.map((c) => `<span style="display:inline-block; border:1px solid rgba(148,163,184,.35); border-radius:6px; padding:0 5px; margin:2px 4px 0 0; font-size:10.5px;">${esc(c)}</span>`).join("")}</div>` : ""}
          </div>
          <div style="display:flex; gap:4px; flex-wrap:wrap; flex-shrink:0;">
            <button class="btn small" data-ehy-zoom="1">Zoom</button>
            <button class="btn small" data-ehy-outline="1">Outline</button>
            ${dataUrl ? `<button class="btn small" data-ehy-import-url="${esc(dataUrl)}" data-ehy-import-name="${esc(name)}" data-ehy-import-proj="${esc(sourceProjection)}">Import</button>` : ""}
          </div>
        </div>
        <div class="row" style="gap:10px; margin-top:4px;">
          ${dataUrl ? `<a class="small" href="${esc(dataUrl)}" target="_blank" rel="noopener noreferrer">Raw ZIP</a>` : ""}
          ${plotUrl ? `<a class="small" href="${esc(plotUrl)}" target="_blank" rel="noopener noreferrer">Plot sheet (PDF)</a>` : ""}
          <details class="small" style="margin-left:auto;"><summary style="cursor:pointer;">Attributes</summary>${rawAttrsPopup("", attrs)}</details>
        </div>
      </div>
    `;
  }

  function soundingsOptionsHtml() {
    if (!soundingsLayers.length) return `<option value="">No Soundings layers yet</option>`;
    return soundingsLayers.map((l) => `<option value="${esc(l.layerId)}" ${l.layerId === exportForm.layerId ? "selected" : ""}>${esc(l.name)} (${Number(l.featureCount || 0).toLocaleString()} pts)</option>`).join("");
  }

  function render() {
    if (!containerEl) return;
    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted); padding-bottom:8px;">
        USACE eHydro condition-survey catalog: search live survey jobs, pull their data (XYZ, geodatabase,
        shapefile) and metadata into HydroTools, or export soundings as an eHydro-named XYZ package.
      </div>
      <div class="subtoolset-list" style="--subtoolset-bleed-top:0px;">

        <details class="toolset-group" open>
          <summary>Survey catalog</summary>
          <div class="toolset-content">
            <div class="row" style="gap:6px; flex-wrap:wrap;">
              <input id="ehy-f-text" type="search" placeholder="Survey/project name contains…" value="${esc(catalogFilters.text)}" style="flex:1; min-width:140px;" />
              <input id="ehy-f-district" list="ehy-district-list" placeholder="District (e.g. CELRL)" value="${esc(catalogFilters.district)}" style="width:150px;" />
              <datalist id="ehy-district-list">${districtOptions.map((d) => `<option value="${esc(d)}">`).join("")}</datalist>
              <select id="ehy-f-type">
                <option value="">Any survey type</option>
                ${Object.entries(SURVEY_TYPE_LABELS).map(([k, v]) => `<option value="${k}" ${catalogFilters.surveyType === k ? "selected" : ""}>${k} - ${v}</option>`).join("")}
              </select>
            </div>
            <div class="row" style="gap:6px; flex-wrap:wrap; margin-top:6px; align-items:center;">
              <label class="small">From <input id="ehy-f-from" type="date" value="${esc(catalogFilters.dateFrom)}" /></label>
              <label class="small">To <input id="ehy-f-to" type="date" value="${esc(catalogFilters.dateTo)}" /></label>
              <label class="small" style="display:inline-flex; align-items:center; gap:6px;">
                <input type="checkbox" id="ehy-f-view" ${catalogFilters.withinView ? "checked" : ""} /> Only within current map view
              </label>
              <button id="ehy-search" class="btn" style="margin-left:auto;">${catalogLoading ? "Searching…" : "Search"}</button>
            </div>
            ${catalogError ? `<div class="small" style="color:#ef4444; margin-top:6px;">${esc(catalogError)}</div>` : ""}
            <div class="small" style="margin-top:6px; color:var(--muted);">
              ${catalogTotalCount != null ? `${catalogTotalCount.toLocaleString()} survey(s) match` : (catalogResults.length ? `${catalogResults.length} loaded` : "No search run yet")}
            </div>
            <div style="margin-top:6px; max-height:420px; overflow:auto;">
              ${catalogResults.map(catalogRowHtml).join("") || `<div class="small" style="color:var(--muted);">Run a search to browse surveys.</div>`}
            </div>
            ${catalogResults.length ? `<div class="row" style="margin-top:6px; justify-content:center;"><button id="ehy-more" class="btn small" ${catalogHasMore ? "" : "disabled"}>${catalogHasMore ? (catalogLoading ? "Loading…" : "Load more") : "No more results"}</button></div>` : ""}
          </div>
        </details>

        <details class="toolset-group">
          <summary>Quick layers &amp; map click-picker</summary>
          <div class="toolset-content">
            <label class="small" style="display:inline-flex; align-items:center; gap:8px;">
              <input type="checkbox" id="ehydro-stack-pick-enabled" ${stackPickEnabled ? "checked" : ""} />
              Click map to list all overlapping surveys
            </label>
            <div style="margin-top:8px; display:flex; flex-direction:column; gap:8px;">
              ${LAYERS.map((def) => {
                const loaded = isLoaded(def);
                return `
                  <div class="row lm-add-row" style="justify-content:space-between; align-items:center;">
                    <div class="small" style="min-width:0;">
                      <div style="font-weight:600;">${esc(def.name)}</div>
                    </div>
                    <div style="display:flex; gap:6px;">
                      <button class="btn"${loaded ? " disabled" : ""} data-ehydro-add="${esc(def.id)}">Add</button>
                      <button class="btn"${loaded ? "" : " disabled"} data-ehydro-zoom="${esc(def.id)}">Zoom</button>
                      <button class="btn"${loaded ? "" : " disabled"} data-ehydro-remove="${esc(def.id)}">Remove</button>
                    </div>
                  </div>
                `;
              }).join("")}
            </div>
          </div>
        </details>

        <details class="toolset-group">
          <summary>Export soundings for eHydro</summary>
          <div class="toolset-content">
            <div class="small" style="color:var(--muted); margin-bottom:6px;">
              Packages a HydroTools Soundings layer as an eHydro-named ASCII XYZ file (Easting Northing
              Depth), matching USACE's required filename convention and Hypack's "eHydro Naming" export
              mode. This does not produce the ISO 19115/FGDC metadata XML or Esri geodatabase a district
              submission may also require.
            </div>
            <div class="row" style="gap:6px; flex-wrap:wrap;">
              <select id="ehy-x-layer" style="flex:1; min-width:160px;">${soundingsOptionsHtml()}</select>
              <select id="ehy-x-type">${Object.entries(SURVEY_TYPE_LABELS).map(([k, v]) => `<option value="${k}" ${exportForm.surveyType === k ? "selected" : ""}>${k} - ${v}</option>`).join("")}</select>
            </div>
            <div class="row" style="gap:6px; flex-wrap:wrap; margin-top:6px;">
              <input id="ehy-x-project" placeholder="Project code" value="${esc(exportForm.projectCode)}" style="width:110px;" />
              <input id="ehy-x-unique" placeholder="Unique ID" value="${esc(exportForm.uniqueId)}" style="width:90px;" />
              <input id="ehy-x-subproject" placeholder="Subproject" value="${esc(exportForm.subproject)}" style="width:110px;" />
              <input id="ehy-x-desc" placeholder="Optional description" value="${esc(exportForm.description)}" style="flex:1; min-width:120px;" />
            </div>
            <div class="row" style="gap:6px; flex-wrap:wrap; margin-top:6px; align-items:center;">
              <span class="small">Export CRS</span>
              ${crsPickerField("ehy-x-crs", exportForm.crs, { style: "flex:1; min-width:160px;" })}
              <select id="ehy-x-depth-unit">
                <option value="ft" ${exportForm.depthUnit === "ft" ? "selected" : ""}>Depth: US survey ft</option>
                <option value="ftIntl" ${exportForm.depthUnit === "ftIntl" ? "selected" : ""}>Depth: international ft</option>
                <option value="m" ${exportForm.depthUnit === "m" ? "selected" : ""}>Depth: meters</option>
              </select>
              <label class="small">Precision <input id="ehy-x-precision" type="number" min="0" max="6" value="${esc(exportForm.precision)}" style="width:48px;" /></label>
            </div>
            <div class="row" style="gap:6px; flex-wrap:wrap; margin-top:6px; align-items:center;">
              <input id="ehy-x-vdatum" placeholder="Vertical datum note (free text, e.g. NAVD88)" value="${esc(exportForm.verticalDatum)}" style="flex:1; min-width:160px;" />
              <label class="small" style="display:inline-flex; align-items:center; gap:6px;">
                <input type="checkbox" id="ehy-x-thinned" ${exportForm.thinned ? "checked" : ""} /> Also write thinned _P.XYZ every
              </label>
              <input id="ehy-x-thinfactor" type="number" min="2" value="${esc(exportForm.thinFactor)}" style="width:50px;" ${exportForm.thinned ? "" : "disabled"} /> <span class="small">pts</span>
            </div>
            <div class="row" style="margin-top:8px;">
              <button id="ehy-x-run" class="btn primary" ${exportBusy ? "disabled" : ""}>${exportBusy ? "Exporting…" : "Export ZIP"}</button>
            </div>
            ${exportStatus ? `<div class="small" style="margin-top:6px; color:#cbd5e1;">${esc(exportStatus)}</div>` : ""}
          </div>
        </details>

      </div>

      <div style="margin-top:8px; ${importProgress.active ? "" : "display:none;"}">
        <div style="height:8px; border:1px solid #334155; border-radius:999px; overflow:hidden; background:#0b1220;">
          <div style="height:100%; width:${Math.round((Number(importProgress.ratio) || 0) * 100)}%; background:linear-gradient(90deg,#38bdf8,#22d3ee); transition:width 120ms linear;"></div>
        </div>
        <div class="small" style="margin-top:4px; color:#cbd5e1;">${esc(importProgress.message || "Working...")}</div>
      </div>
      <div class="small" style="margin-top:8px; color:#cbd5e1;">${esc(status)}</div>
      ${importLog.length ? `
        <details style="margin-top:6px;">
          <summary class="small" style="cursor:pointer;">Import log (${importLog.length})</summary>
          ${importLog.map((e) => `<div class="small" style="margin-top:4px; padding:4px 0; border-top:1px solid rgba(148,163,184,.2);"><b>${e.ok ? "✓" : "✗"} ${esc(e.title)}</b> - ${esc(e.message)}</div>`).join("")}
        </details>
      ` : ""}
    `;
    enhanceCrsPickers(containerEl, app);
  }

  function debouncedSearch() {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => runCatalogSearch({ reset: true }), 350);
  }

  function onInput(event) {
    const id = event.target?.id;
    if (id === "ehy-f-text") { catalogFilters.text = event.target.value; debouncedSearch(); return; }
    if (id === "ehy-f-district") { catalogFilters.district = event.target.value; debouncedSearch(); return; }
    if (id === "ehy-x-precision") { exportForm.precision = event.target.value; return; }
    if (id === "ehy-x-thinfactor") { exportForm.thinFactor = event.target.value; return; }
    if (["ehy-x-project", "ehy-x-unique", "ehy-x-subproject", "ehy-x-desc", "ehy-x-vdatum"].includes(id)) {
      const map = { "ehy-x-project": "projectCode", "ehy-x-unique": "uniqueId", "ehy-x-subproject": "subproject", "ehy-x-desc": "description", "ehy-x-vdatum": "verticalDatum" };
      exportForm[map[id]] = event.target.value;
    }
  }

  function onChange(event) {
    const t = event.target;
    const id = t?.id;
    if (id === "ehy-f-type") { catalogFilters.surveyType = t.value; runCatalogSearch({ reset: true }); return; }
    if (id === "ehy-f-from") { catalogFilters.dateFrom = t.value; runCatalogSearch({ reset: true }); return; }
    if (id === "ehy-f-to") { catalogFilters.dateTo = t.value; runCatalogSearch({ reset: true }); return; }
    if (id === "ehy-f-view") { catalogFilters.withinView = !!t.checked; runCatalogSearch({ reset: true }); return; }
    if (id === "ehy-x-layer") { exportForm.layerId = t.value; return; }
    if (id === "ehy-x-type") { exportForm.surveyType = t.value; return; }
    if (id === "ehy-x-depth-unit") { exportForm.depthUnit = t.value; return; }
    if (id === "ehy-x-thinned") { exportForm.thinned = !!t.checked; render(); return; }
    if (id === "ehy-x-crs") { exportForm.crs = t.value; return; }
    if (id === "ehydro-stack-pick-enabled") { stackPickEnabled = !!t.checked; status = stackPickEnabled ? "Stacked picker enabled." : "Stacked picker disabled."; render(); return; }
  }

  function onKeydown(event) {
    if (event.key === "Enter" && event.target?.closest?.("#ehy-f-text, #ehy-f-district")) {
      event.preventDefault();
      clearTimeout(searchDebounce);
      runCatalogSearch({ reset: true });
    }
  }

  function onClick(event) {
    const addId = event.target?.closest?.("[data-ehydro-add]")?.getAttribute?.("data-ehydro-add");
    if (addId) { const def = LAYERS.find((it) => it.id === addId); if (def) addLayer(def); return; }
    const removeId = event.target?.closest?.("[data-ehydro-remove]")?.getAttribute?.("data-ehydro-remove");
    if (removeId) { const def = LAYERS.find((it) => it.id === removeId); if (def) removeLayer(def); return; }
    const zoomId = event.target?.closest?.("[data-ehydro-zoom]")?.getAttribute?.("data-ehydro-zoom");
    if (zoomId) {
      const svc = layerService();
      const def = LAYERS.find((it) => it.id === zoomId);
      if (!svc || !def) return;
      const existing = existingByMetaId(def.id);
      if (!existing?.id) { status = `${def.name} is not loaded.`; render(); return; }
      try { svc.zoomTo(existing.id); status = `Zoomed to ${def.name}.`; } catch { status = `Could not zoom to ${def.name}.`; }
      render();
      return;
    }
    if (event.target?.closest?.("#ehy-search")) { clearTimeout(searchDebounce); runCatalogSearch({ reset: true }); return; }
    if (event.target?.closest?.("#ehy-more")) { runCatalogSearch({ reset: false }); return; }
    if (event.target?.closest?.("#ehy-x-run")) { runExport(); return; }
    const row = event.target?.closest?.(".card");
    if (row && event.target?.closest?.("[data-ehy-zoom]")) {
      const idx = [...containerEl.querySelectorAll(".card")].indexOf(row);
      if (catalogResults[idx]) zoomToFeature(catalogResults[idx]);
      return;
    }
    if (row && event.target?.closest?.("[data-ehy-outline]")) {
      const idx = [...containerEl.querySelectorAll(".card")].indexOf(row);
      if (catalogResults[idx]) addFeatureOutline(catalogResults[idx]);
      return;
    }
    const importBtn = event.target?.closest?.("[data-ehy-import-url]");
    if (importBtn) {
      const href = importBtn.getAttribute("data-ehy-import-url") || "";
      const name = importBtn.getAttribute("data-ehy-import-name") || "eHydro Survey";
      const sourceProjection = importBtn.getAttribute("data-ehy-import-proj") || "";
      runImport({ sourceUrl: href, name, sourceProjection, lat: null, lon: null });
    }
  }

  function onSoundingsCatalogChanged({ layers } = {}) {
    soundingsLayers = Array.isArray(layers) ? layers : [];
    if (!exportForm.layerId && soundingsLayers.length) exportForm.layerId = soundingsLayers[0].layerId;
    render();
  }

  let offSoundingsCatalog = null;

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("keydown", onKeydown);
    }
    containerEl = el;
    render();
    ensureMapClickBinding();
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onChange);
    containerEl.addEventListener("keydown", onKeydown);
    if (!districtOptions.length) loadDistrictOptions().then((opts) => { districtOptions = opts; render(); });
    if (!offSoundingsCatalog) offSoundingsCatalog = app.events.on("soundings:catalog:changed", onSoundingsCatalogChanged);
    refreshSoundingsLayers();
  }

  function destroy() {
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("change", onChange);
    containerEl.removeEventListener("keydown", onKeydown);
    removeMapClickBinding();
    offSoundingsCatalog?.();
    offSoundingsCatalog = null;
    containerEl = null;
  }

  return {
    mount,
    destroy,
    snapshot: () => ({}),
    restore: () => {},
  };
}
